import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "@material-ui/core";
import { ModalComponent } from "components/ui/modal/Modal";
import ReportList from "components/data-display/report-list/ReportList";
import html2canvas from "html2canvas";

import "./screenshot.css";

const HINT_DURATION = 5000;

export function Screenshot({
  tagReference,
  folderPath,
  customCapture,
  variant,
  hintPlacement
}) {
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [image, setImage] = useState({ img: null, base64: "" });

  // Capture hint
  const reports = useSelector(state => state.report?.report || []);
  const prevCountRef = useRef(reports.length);
  const [showHint, setShowHint] = useState(false);
  const [hintLeaving, setHintLeaving] = useState(false);
  const [hintStyle, setHintStyle] = useState({});
  const [hintArrow, setHintArrow] = useState("bottom");
  const toolbarRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const dismissHint = () => {
    setHintLeaving(true);
    setTimeout(() => { setShowHint(false); setHintLeaving(false); }, 260);
  };

  // Compute hint position from the toolbar's bounding rect
  const computeHintPos = () => {
    if (!toolbarRef.current) return;
    const rect = toolbarRef.current.getBoundingClientRect();
    const isInline = variant === "inline";

    if (isInline) {
      const placement = hintPlacement || "leftOfToolbar";
      const hintWidth = 256; // keep in sync with CSS

      // Default behavior (legacy): hint aparece do lado esquerdo (seta para a direita)
      if (placement === "leftOfToolbar") {
        setHintArrow("right");
        setHintStyle({
          position: "fixed",
          top: Math.max(8, rect.top + rect.height / 2 - 40),
          right: window.innerWidth - rect.left + 12,
          left: "auto",
          bottom: "auto"
        });
        return;
      }

      // New behavior: hint aparece do lado direito (seta para a esquerda)
      setHintArrow("left");
      const unclampedLeft = rect.left + rect.width + 12;
      const clampedLeft = Math.max(8, Math.min(unclampedLeft, window.innerWidth - hintWidth - 8));

      setHintStyle({
        position: "fixed",
        top: Math.max(8, rect.top + rect.height / 2 - 40),
        left: clampedLeft,
        right: "auto",
        bottom: "auto"
      });
    } else {
      // Toolbar is fixed at bottom — hint appears ABOVE
      setHintArrow("bottom");
      setHintStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 10,
        left: rect.left,
        top: "auto",
        right: "auto",
      });
    }
  };

  useEffect(() => {
    if (reports.length > prevCountRef.current) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      computeHintPos();
      setShowHint(true);
      setHintLeaving(false);
      dismissTimerRef.current = setTimeout(dismissHint, HINT_DURATION);
    }
    prevCountRef.current = reports.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports.length]);

  useEffect(() => {
    return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
  }, []);

  function handleOpen() { setIsOpenModal(true); }
  function handleClose() { setIsOpenModal(false); }

  const openModal = async e => {
    e.preventDefault();
    setIsLoadingModal(true);
    try {
      if (tagReference.current) {
        let screenshot;
        if (
          tagReference.current.components &&
          tagReference.current.components.screenshot
        ) {
          screenshot = tagReference.current.components.screenshot.getCanvas("perspective");
        } else {
          try {
            const canvas = await html2canvas(tagReference.current, {
              useCORS: true,
              logging: false
            });
            screenshot = canvas;
          } catch (canvasErr) {
            return;
          }
        }

        const image64 = screenshot.toDataURL();
        const imageJpg = await new Promise(resolve => {
          screenshot.toBlob(
            blob => {
              if (!blob) { resolve(null); return; }
              resolve(new File([blob], "screenshot.jpg", { type: "image/jpeg" }));
            },
            "image/jpeg",
            1
          );
        });

        setImage({ img: imageJpg, base64: image64 });
      }
      handleOpen();
    } catch (err) {
    } finally {
      setIsLoadingModal(false);
    }
  };

  const toolbarClass = [
    "screenshot-toolbar",
    variant ? `screenshot-toolbar--${variant}` : "",
    showHint ? "screenshot-toolbar--hint" : ""
  ].filter(Boolean).join(" ");

  return (
    <>
      <div ref={toolbarRef} className={toolbarClass}>
        {isLoadingModal ? (
          <div className="screenshot-toolbar__spinner" role="status">
            <div className="spinner-border text-primary" />
          </div>
        ) : (
          <Tooltip title="Capturar e criar ocorrência (anote com lápis, círculo, quadrado e cores)">
            <button
              type="button"
              onClick={openModal}
              className="screenshot-toolbar__btn"
              aria-label="Capturar snapshot e anotar"
            >
              <i className="fas fa-camera" />
            </button>
          </Tooltip>
        )}
        <ReportList folderPath={folderPath} />
      </div>

      {showHint && createPortal(
        <div
          className={`capture-hint capture-hint--arrow-${hintArrow}${hintLeaving ? " capture-hint--leaving" : ""}`}
          style={{ ...hintStyle, "--hint-duration": `${HINT_DURATION}ms` }}
          onClick={dismissHint}
          role="status"
          aria-live="polite"
        >
          <div className="capture-hint__body">
            <div className="capture-hint__check">
              <i className="fas fa-check" />
            </div>
            <div className="capture-hint__text">
              <span className="capture-hint__title">Captura registrada</span>
              <span className="capture-hint__sub">
                Clique em <i className="fas fa-file-alt" /> para revisar e salvar
              </span>
            </div>
            <div className="capture-hint__arrow">
              <i className="fas fa-chevron-right" />
            </div>
          </div>
          <div className="capture-hint__timer">
            <div className="capture-hint__timer-fill" />
          </div>
        </div>,
        document.body
      )}

      {createPortal(
        <ModalComponent
          show={isOpenModal}
          closeModal={handleClose}
          image={image}
          variant={variant}
        />,
        document.body
      )}
    </>
  );
}
